<template>
    <div class="music-sidebar" :style="width ? {width: width + 'px'} : null">
        <div class="music-sidebar-nav">
            <div class="music-nav-segment">
                <button :class="['music-nav-segment-btn', {'music-nav-segment-btn--active': ms.state.activeTab === 'radio'}]"
                        @click="onNavClick('radio')">
                    <svg class="music-nav-segment-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="2"/><path d="M16.24 7.76a6 6 0 0 1 0 8.49m-8.48-.01a6 6 0 0 1 0-8.49m11.31-2.82a10 10 0 0 1 0 14.14m-14.14 0a10 10 0 0 1 0-14.14"/></svg>
                    {{ $t('common.music.radio') }}
                </button>
                <button :class="['music-nav-segment-btn', {'music-nav-segment-btn--active': ms.state.activeTab === 'library'}]"
                        @click="onNavClick('library')">
                    <svg class="music-nav-segment-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/></svg>
                    {{ $t('common.music.myMusic') }}
                </button>
                <span class="music-nav-segment-bg" :style="{transform: ms.state.activeTab !== 'radio' ? 'translateX(100%)' : ''}"></span>
            </div>
        </div>

        <div class="music-sidebar-library-list">
            <div :class="['music-playlist-item', {'music-playlist-item--active': ms.state.activeTab === 'library' && !ms.state.activePlaylistId}]"
                 @click="onNavClick('library')">
                <span class="music-playlist-avatar music-playlist-avatar--library">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
                </span>
                <span class="music-playlist-name">{{ $t('common.music.library') }}</span>
                <span v-if="libraryTrackCount != null" class="music-playlist-count">{{ libraryTrackCount }}</span>
            </div>
        </div>

        <div class="music-sidebar-section-header">
            <span class="music-sidebar-section-title">{{ $t('common.music.playlists') }}</span>
            <button class="music-sidebar-add-btn" @click="startCreate">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            </button>
        </div>

        <div class="music-sidebar-playlists">
            <template v-if="ms.state.playlistsLoading && !playlists.length">
                <div v-for="i in 4" :key="'skel-'+i" class="music-playlist-item music-skeleton-item">
                    <span class="music-skeleton-circle"></span>
                    <span class="music-skeleton-text" :style="{width: (45 + i * 12) + '%'}"></span>
                </div>
            </template>
            <template v-else>
                <div v-for="pl in playlists" :key="pl.id"
                     :class="['music-playlist-item', {
                         'music-playlist-item--active': isActive(pl),
                         'music-playlist-item--drop-target': dropTargetId === pl.id
                     }]"
                     @click="onPlaylistClick(pl)"
                     @contextmenu.prevent="!pl.isLibrary && toggleMenu(pl.id)"
                     @dragover.prevent="onPlaylistDragOver($event, pl)"
                     @dragleave="onPlaylistDragLeave(pl)"
                     @drop="onPlaylistDrop($event, pl)">
                    <span class="music-playlist-avatar" :style="{background: playlistColor(pl.id)}">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M21 15V6"/><path d="M18.5 18a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z"/><path d="M12 12H3"/><path d="M16 6H3"/><path d="M12 18H3"/></svg>
                    </span>
                    <template v-if="editingId === pl.id">
                        <input ref="renameInput" class="music-playlist-input" type="text"
                               v-model="editingName"
                               @keydown.enter="saveRename(pl)"
                               @keydown.esc="cancelEdit"
                               @blur="saveRename(pl)"
                               @click.stop>
                    </template>
                    <template v-else>
                        <span class="music-playlist-name">{{ pl.name }}</span>
                        <span class="music-playlist-count">{{ pl.trackCount }}</span>
                        <button v-if="!pl.isLibrary" class="music-playlist-menu-btn"
                                @click.stop="toggleMenu(pl.id)">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/></svg>
                        </button>
                    </template>
                    <div v-if="menuId === pl.id" class="music-playlist-dropdown" @click.stop>
                        <button class="music-playlist-dropdown-item" @click="startRename(pl)">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>
                            {{ $t('common.music.renamePlaylist') }}
                        </button>
                        <button class="music-playlist-dropdown-item music-playlist-dropdown-item--danger" @click="confirmDelete(pl)">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                            {{ $t('common.music.deletePlaylist') }}
                        </button>
                    </div>
                </div>
                <div v-if="!playlists.length && !creating" class="music-sidebar-empty">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 15V6"/><path d="M18.5 18a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z"/><path d="M12 12H3"/><path d="M16 6H3"/><path d="M12 18H3"/></svg>
                    <span>{{ $t('common.music.noPlaylists') }}</span>
                </div>
            </template>
            <div v-if="creating" class="music-playlist-item">
                <span class="music-playlist-avatar music-playlist-avatar--new">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                </span>
                <input ref="createInput" class="music-playlist-input" type="text"
                       v-model="newName"
                       :placeholder="$t('common.music.playlistName')"
                       @keydown.enter="saveCreate"
                       @keydown.esc="cancelEdit"
                       @blur="saveCreate">
            </div>
        </div>

        <div v-if="!isCompact" class="music-sidebar-resize" @mousedown.prevent="$emit('divider-start', $event)" @touchstart.prevent="$emit('divider-start', $event)"></div>
    </div>
</template>

<script>
import musicService from './musicService'

export default {
    name: 'MusicSidebar',
    inject: ['panelVm'],
    props: {
        width: { type: Number, default: null },
    },
    emits: ['divider-start', 'navigate'],
    data() {
        return {
            menuId: null,
            editingId: null,
            editingName: '',
            creating: false,
            newName: '',
            dropTargetId: null,
        }
    },
    created() {
        this._saving = false
    },
    computed: {
        ms() { return musicService },
        isCompact() { return this.panelVm?.isCompact },
        playlists() {
            return musicService.state.playlists.filter(p => !p.isLibrary)
        },
        libraryTrackCount() {
            const lib = musicService.state.playlists.find(p => p.isLibrary)
            return lib ? lib.trackCount : null
        },
    },
    methods: {
        playlistColor(id) {
            const colors = ['#6366F1', '#EC4899', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EF4444', '#14B8A6']
            return colors[id % colors.length]
        },
        isActive(pl) {
            return musicService.state.activeTab === 'library' && musicService.state.activePlaylistId === pl.id
        },
        onNavClick(tab) {
            if (tab === 'library') {
                musicService.setActiveTab('library')
                musicService.setActivePlaylist(null)
            } else {
                musicService.setActiveTab('radio')
            }
            this.$emit('navigate')
        },
        onPlaylistClick(pl) {
            if (this.editingId === pl.id) return
            musicService.setActiveTab('library')
            musicService.setActivePlaylist(pl.id)
            this.$emit('navigate')
        },
        toggleMenu(id) {
            this.menuId = this.menuId === id ? null : id
        },
        startCreate() {
            this.cancelEdit()
            this.creating = true
            this.newName = ''
            this.$nextTick(() => this.$refs.createInput?.focus())
        },
        async saveCreate() {
            if (this._saving) return
            this._saving = true
            const name = this.newName.trim()
            this.creating = false
            if (name) await musicService.createPlaylist(name)
            this._saving = false
        },
        startRename(pl) {
            this.menuId = null
            this.editingId = pl.id
            this.editingName = pl.name
            this.$nextTick(() => {
                const input = this.$refs.renameInput
                if (Array.isArray(input)) input[0]?.focus()
                else input?.focus()
            })
        },
        async saveRename(pl) {
            if (this._saving) return
            this._saving = true
            const name = this.editingName.trim()
            this.editingId = null
            if (name && name !== pl.name) await musicService.updatePlaylist(pl.id, { name })
            this._saving = false
        },
        confirmDelete(pl) {
            this.menuId = null
            this.eventBus.$emit('showConfirmDialog', {
                title: this.$t('common.music.deletePlaylist'),
                message: this.$t('common.music.deletePlaylistConfirm', { name: pl.name }),
                confirmText: this.$t('common.music.deletePlaylist'),
                confirmType: 'danger',
                onConfirm: () => {
                    musicService.deletePlaylist(pl.id)
                },
            })
        },
        cancelEdit() {
            this.editingId = null
            this.creating = false
            this.menuId = null
        },
        onPlaylistDragOver(e, pl) {
            if (!e.dataTransfer.types.includes('music/track-id')) return
            e.dataTransfer.dropEffect = 'copy'
            this.dropTargetId = pl.id
        },
        onPlaylistDragLeave(pl) {
            if (this.dropTargetId === pl.id) this.dropTargetId = null
        },
        async onPlaylistDrop(e, pl) {
            this.dropTargetId = null
            const trackId = e.dataTransfer.getData('music/track-id')
            if (!trackId) return
            await musicService.addTrackToPlaylist(parseInt(trackId), pl.id)
        },
        onDocClick(e) {
            if (this.menuId && !e.target.closest('.music-playlist-dropdown') && !e.target.closest('.music-playlist-menu-btn')) {
                this.menuId = null
            }
        },
    },
    mounted() {
        document.addEventListener('mousedown', this.onDocClick)
    },
    beforeUnmount() {
        document.removeEventListener('mousedown', this.onDocClick)
    },
}
</script>
